import React, { useCallback } from "react";
import ExcelJS from "exceljs";
import moment from "moment";
import excelIcon from "../../img/whiteexcel.png";
import { useSnackbar } from "./SnackbarProvider";
import { formatCellValue } from "./ExcelPdfFormateCellValue";
import QRCode from "qrcode"; // Add this import

// Centralized status display mapping — keeps UI + Excel consistent
const STATUS_DISPLAY_MAP = {
  INVALID: "UNDESIGNATED",
  COMPLETED: "DESIGNATED",
};

const CommanExcelExport = ({
  headers = [],
  rows = [],
  exportHeading = [],
  fromDate,
  toDate,
  showTripSummary = false,
  vehicleNumber,
  siteName,
  reportGeneratedBy,
  innerTable = false, // New prop to enable inner table structure
  foruptimeMonthly = false, // Add this prop
  innerTableforVehicleInteractionNotesList = false, // Add this prop
  isGroupByDistanceRange = false, // Add this line

  isGroupByTransporter = false,
  outerTableData = [],
  outerHeaders = [],
  innerHeaders = [],
  reportType = "",
  multiHeader = false, // Add this
  nestedHeadersConfig = {}, // Add this
  hierarchyList = [],

  // showQR = false,
  // scannerURL = "",
}) => {
  const { showSnackbar } = useSnackbar();

  const borderStyle = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  // Helper function to extract nested values safely
  const getNestedValue = (obj, path, defaultValue = "") => {
    try {
      const value = path
        .split(".")
        .reduce((acc, part) => acc && acc[part], obj);
      return value || defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };

  const toNumberOrValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "number") {
      // Keep long numbers as strings to prevent E+14 notation
      if (String(value).replace("-", "").replace(".", "").length > 11) {
        return String(value);
      }
      return value;
    }
    const str = String(value).trim();
    if (str === "") return "";

    // 🔑 KEY FIX: If it's a long numeric identifier (>= 12 digits), keep as string
    if (/^\d{12,}$/.test(str)) {
      return str;
    }

    const num = Number(str);
    if (!isNaN(num) && isFinite(num)) {
      return num;
    }
    return value;
  };

  const mapStatusValue = (value) => {
    if (typeof value === "string") {
      const upper = value.trim().toUpperCase();
      return STATUS_DISPLAY_MAP[upper] || value;
    }
    return value;
  };

  // const generateQRCodeAsDataURL = async (url) => {
  //   try {
  //     const qrCodeDataURL = await QRCode.toDataURL(url, {
  //       width: 400, // Increased from 60 to 400 pixels
  //       margin: 1, // Minimal margin
  //       errorCorrectionLevel: "H", // Highest error correction (30%)
  //       color: {
  //         dark: "#000000", // Pure black for better contrast
  //         light: "#ffffff", // Pure white background
  //       },
  //     });
  //     return qrCodeDataURL;
  //   } catch (error) {
  //     console.error("Error generating QR code:", error);
  //     return null;
  //   }
  // };

  // const addQRReferenceToWorksheet = async (
  //   worksheet,
  //   currentRow,
  //   colCount,
  //   workbook
  // ) => {
  //   if (!showQR || !scannerURL) return currentRow;

  //   try {
  //     // Generate QR code
  //     const qrCodeDataURL = await generateQRCodeAsDataURL(scannerURL);

  //     if (!qrCodeDataURL) {
  //       // Fallback: just show the URL as text (no link)
  //       const urlCell = worksheet.getCell(currentRow, 1);
  //       urlCell.value = `QR Code Reference: ${scannerURL}`;
  //       return currentRow + 1;
  //     }

  //     // Add QR Code title
  //     const titleRow = currentRow;
  //     worksheet.mergeCells(titleRow, 1, titleRow, colCount);
  //     const titleCell = worksheet.getCell(titleRow, 1);
  //     titleCell.value = "QR Code Reference";
  //     Object.assign(titleCell, {
  //       font: { bold: true, size: 12, color: { argb: "003A6B" } },
  //       alignment: { horizontal: "center", vertical: "middle" },
  //       border: borderStyle,
  //     });
  //     currentRow++;
  //     // Add QR code image to Excel
  //     const base64Data = qrCodeDataURL.split(",")[1]; // Remove data URL prefix

  //     const imageId = workbook.addImage({
  //       base64: base64Data,
  //       extension: "png",
  //     });

  //     // Calculate position to center the QR code
  //     const qrColStart = Math.max(1, Math.floor((colCount - 2) / 2)); // Adjusted for 2 columns
  //     const qrRowStart = currentRow;

  //     // Add the image spanning 2 columns and 6 rows
  //     worksheet.addImage(imageId, {
  //       tl: { col: qrColStart, row: qrRowStart },
  //       br: { col: qrColStart + 2, row: qrRowStart + 6 },
  //     });

  //     // Adjust row heights for QR code area
  //     for (let i = 0; i < 6; i++) {
  //       const row = worksheet.getRow(currentRow + i);
  //       if (row) {
  //         row.height = 18;
  //       }
  //     }

  //     currentRow += 7; // Move past the QR code

  //     return currentRow + 1; // Add extra spacing
  //   } catch (error) {
  //     console.error("Error adding QR to Excel:", error);
  //     // Simple fallback - just show the URL as text
  //     const urlCell = worksheet.getCell(currentRow, 1);
  //     urlCell.value = `QR Reference: ${scannerURL}`;
  //     return currentRow + 1;
  //   }
  // };
  const getDuration = (startTime, endTime) => {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    const diffMs = endDate - startDate; // difference in milliseconds

    const seconds = Math.floor((diffMs / 1000) % 60);
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Usage

  // Format location for display
  const formatLocation = (row) => {
    if (row.address) return row.address;
    if (row.latitude && row.longitude) {
      return `${row.latitude.toFixed(6)}, ${row.longitude.toFixed(6)}`;
    }
    return "N/A";
  };

  const createTitleRow = (worksheet, title, colCount, dateData) => {
    worksheet.mergeCells(1, 1, 1, colCount);
    const cell = worksheet.getCell(1, 1);
    cell.value = `${title} ${dateData || ""}`;
    Object.assign(cell, {
      font: { bold: true, size: 16, underline: true },
      alignment: { horizontal: "center", vertical: "middle", wrapText: true },
      border: borderStyle,
    });
    worksheet.getRow(1).height = 28;
  };

  const createVehicleSiteInfoRow = (
    worksheet,
    colCount,
    vehicleNumber,
    siteName,
  ) => {
    if (!vehicleNumber && !siteName) return 0;

    worksheet.mergeCells(2, 1, 2, colCount);
    const cell = worksheet.getCell(2, 1);

    let infoText = "";
    if (vehicleNumber && siteName) {
      infoText = `Vehicle No: ${vehicleNumber} | Area: ${siteName}`;
    } else if (vehicleNumber) {
      infoText = `Vehicle: ${vehicleNumber}`;
    } else if (siteName) {
      infoText = `Site: ${siteName}`;
    }

    cell.value = infoText;
    Object.assign(cell, {
      font: { bold: true, size: 12, color: { argb: "000000" } },
      alignment: { horizontal: "center", vertical: "middle", wrapText: true },
      border: borderStyle,
    });
    worksheet.getRow(2).height = 20;

    return 1; // Return 1 to indicate we added one row
  };

  const createGeneratedRow = (
    worksheet,
    colCount,
    startRow,
    reportGeneratedBy,
  ) => {
    // Report Generated row
    worksheet.mergeCells(startRow, 1, startRow, colCount);
    const cell = worksheet.getCell(startRow, 1);
    cell.value = `Report Generated At : ${moment().format("YYYY-MM-DD hh:mm A")}`;
    Object.assign(cell, {
      font: { bold: true, size: 12, color: { argb: "00008B" } },
      alignment: { horizontal: "center", vertical: "middle", wrapText: true },
      border: borderStyle,
    });
    worksheet.getRow(startRow).height = 20;

    // Report Generated By row (only if present)
    if (reportGeneratedBy) {
      const generatedByRow = startRow + 1;
      worksheet.mergeCells(generatedByRow, 1, generatedByRow, colCount);
      const generatedByCell = worksheet.getCell(generatedByRow, 1);
      generatedByCell.value = `Report Generated By: ${reportGeneratedBy}`;
      Object.assign(generatedByCell, {
        font: { bold: true, size: 12, color: { argb: "00008B" } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: borderStyle,
      });
      worksheet.getRow(generatedByRow).height = 20;

      return 2; // Return 2 to indicate we added two rows
    }

    return 1; // Return 1 to indicate we added one row
  };

  const createTripSummarySection = (worksheet, rows, colCount, startRow) => {
    if (!rows.length) return startRow;

    const tripSummary = rows[0];

    // Trip Summary Title
    worksheet.mergeCells(startRow, 1, startRow, colCount);
    const titleCell = worksheet.getCell(startRow, 1);
    titleCell.value = "Trip Summary";
    Object.assign(titleCell, {
      font: { bold: true, size: 14, color: { argb: "0D3A6B" } },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E6F3FF" },
      },
      alignment: { horizontal: "center", vertical: "middle" },
      border: borderStyle,
    });
    worksheet.getRow(startRow).height = 24;

    // Trip Summary Details
    const summaryDetails = [
      { label: "Vehicle Number", value: tripSummary.vehicleNumber },
      { label: "Trip Duration", value: tripSummary.duration },
      { label: "Status", value: tripSummary.status },
      { label: "Area Name", value: tripSummary.areaName },
      { label: "Start Time", value: tripSummary.startTime },
      { label: "End Time", value: tripSummary.endTime },
      { label: "Source", value: tripSummary.source },
      { label: "Destination", value: tripSummary.destination },
      {
        label: "Total Tracking Points",
        value: tripSummary.totalTrackingPoints,
      },
    ];

    summaryDetails.forEach((detail, index) => {
      const rowNum = startRow + 1 + index;

      // Label cell (Column A)
      const labelCell = worksheet.getCell(rowNum, 1);
      labelCell.value = detail.label;
      Object.assign(labelCell, {
        font: { bold: true, size: 11 },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F5F5F5" },
        },
        alignment: {
          horizontal: "left",
          vertical: "middle",
          wrapText: false,
        },
        border: borderStyle,
      });

      // Value cell (merge from column B to last column)
      worksheet.mergeCells(rowNum, 2, rowNum, colCount);
      const valueCell = worksheet.getCell(rowNum, 2);
      const rawValue =
        detail.label === "Status" ? mapStatusValue(detail.value) : detail.value;
      const summaryVal = toNumberOrValue(rawValue);
      valueCell.value = summaryVal === "" ? "N/A" : summaryVal;
      Object.assign(valueCell, {
        font: { size: 11 },
        alignment: {
          horizontal: "left",
          vertical: "middle",
          wrapText: false,
        },
        border: borderStyle,
      });

      // Set row height to accommodate content
      worksheet.getRow(rowNum).height = 22;
    });

    // Set column A width for labels
    worksheet.getColumn(1).width = 35;

    return startRow + 1 + summaryDetails.length; // Return next row position
  };

  const createHeaderRow = (worksheet, visibleHeaders, startRow) => {
    visibleHeaders.forEach((header, i) => {
      const cell = worksheet.getCell(startRow, i + 1);
      cell.value = header.name || header.keyName || "";
      Object.assign(cell, {
        font: { bold: true },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "CCE5FF" },
        },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: borderStyle,
      });
    });
    worksheet.getRow(startRow).height = 22;
  };

  const createDataRows = (worksheet, visibleHeaders, rows, startRow) => {
    rows.forEach((row, rowIndex) => {
      const excelRow = startRow + rowIndex;
      visibleHeaders.forEach((header, colIndex) => {
        const cell = worksheet.getCell(excelRow, colIndex + 1);

        let cellValue = formatCellValue(row, header, rowIndex);
        if (header?.keyName === "tripStatus" || header?.isBox) {
          cellValue = mapStatusValue(cellValue);
        }

        const processedValue = toNumberOrValue(cellValue);

        cell.value = processedValue;

        // 🔑 KEY FIX: Force text format for long numeric strings (IMEI, etc.)
        if (
          typeof processedValue === "string" &&
          /^\d{12,}$/.test(processedValue)
        ) {
          cell.numFmt = "@"; // Excel text format — prevents E+14
        }

        Object.assign(cell, {
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
    });
  };

  const autoFitColumns = (worksheet, visibleHeaders, rows) => {
    visibleHeaders.forEach((header, c) => {
      const col = worksheet.getColumn(c + 1);
      let maxLen = (header?.name || header?.keyName || "").toString().length;

      rows.forEach((row, r) => {
        const val = formatCellValue(row, header, r);
        maxLen = Math.max(maxLen, String(val || "").length);
      });

      col.width = Math.min(maxLen + 4, 80);
    });
  };

  // Generate inner table structure for alert data
  const generateInnerTableStructure = (worksheet, rows, startRow) => {
    let currentRow = startRow;

    // Outer table headers for vehicle summary
    const outerHeaders = [
      "S. NO",
      "VEHICLE NUMBER",
      "LAST EVENT TIME",
      "TOTAL ALERTS",
    ];

    // Inner table headers for alert details
    const innerHeaders = [
      "S. No",
      "Alert Type",
      "Area",
      "Mine Name",
      "Vehicle Number",
      "Satellite Counte",
      "Vehicle Type",
      "Vehicle Sub Type",
      "Transporter Name",
      "Power Restore Time",
      "Power Restore location",
      "Nearest Landmark",
      "Server Time",
      "Event Time",
      "Firmware Version",
      "Alert Location",
      "GeoFence Enter Time",
      "GeoFence Enter Location",
      "Duration",
      "Speed (Km)",
      "Reason",
      "Remarks",
    ];

    // Combine all headers to calculate consistent column widths
    const allHeaders = [...outerHeaders, ...innerHeaders];
    const columnWidths = Array(allHeaders.length).fill(0);

    // Calculate max width for each column across all data
    allHeaders?.forEach((header, colIndex) => {
      let maxLen = header.toString().length;

      // Check outer table data
      rows?.forEach((vehicle) => {
        const outerData = [
          "", // S.No will be calculated per vehicle
          vehicle.number || "N/A",
          vehicle?.alerts[0]?.createdAt
            ? moment(vehicle?.alerts[0]?.createdAt).format("DD-MM-YYYY HH:mm")
            : "--" || 0,
          vehicle.alerts ? vehicle.alerts.length.toString() : "0",
        ];
        if (outerData[colIndex]) {
          maxLen = Math.max(maxLen, String(outerData[colIndex]).length);
        }

        // Check inner table data
        if (vehicle.alerts) {
          vehicle.alerts.forEach((alert, alertIndex) => {
            const innerData = [
              (alertIndex + 1).toString(),
              alert?.alertType === "POWER_CUT"
                ? "DISCONNECTION_ALERT"
                : alert?.alertType || "N/A", // Alert Type
              getNestedValue(alert, "vehicle.site.name"),
              getNestedValue(alert, "vehicle.mineName"),
              getNestedValue(alert, "vehicle.number"),
              getNestedValue(alert, "vehicle.vehicleActualTypeDTO.name"), // Vehicle Sub Type
              getNestedValue(alert, "vehicle.vehicleType.name"),
              getNestedValue(alert, "vehicle.vendor.name"),
              alert.powercutRestoreTime
                ? moment(alert.powercutRestoreTime).format(
                    "DD-MM-YYYY HH:mm:ss",
                  )
                : "N/A",
              alert?.powercutRestoreLatitude && alert?.powercutRestoreLongitude
                ? `${alert.powercutRestoreLatitude} , ${alert.powercutRestoreLongitude}`
                : "N/A",
              getNestedValue(alert, "nearestLandmark") || "N/A",
              alert.serverTime
                ? moment(alert.serverTime).format("DD-MM-YYYY HH:mm:ss")
                : "N/A",
              alert.createdAt
                ? moment(alert.createdAt).format("DD-MM-YYYY HH:mm:ss")
                : "N/A",
              getNestedValue(alert, "firmwareVersion"),
              formatLocation(alert),
              alert.geoFenceEnterTime
                ? moment(alert.geoFenceEnterTime).format("DD-MM-YYYY HH:mm:ss")
                : "N/A",
              alert.geoFenceLocation || "N/A",
              alert.geoFenceEnterTime
                ? getDuration(alert?.createdAt, alert?.geoFenceEnterTime)
                : "N/A",
              alert?.speed ? alert?.speed?.toFixed(2) : "N/A",
            ];
            if (innerData[colIndex]) {
              maxLen = Math.max(maxLen, String(innerData[colIndex]).length);
            }
          });
        }
      });

      columnWidths[colIndex] = Math.min(maxLen + 4, 80);
    });

    // Apply consistent column widths
    columnWidths.forEach((width, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = width;
    });

    // Generate content for each vehicle
    rows.forEach((vehicle, vehicleIndex) => {
      // Create outer table header for EACH vehicle
      outerHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "003A6B" }, // Navy Blue
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // Outer table row data
      const outerRowData = [
        vehicleIndex + 1,
        vehicle.number || "N/A",
        vehicle?.alerts[0]?.createdAt
          ? moment(vehicle?.alerts[0]?.createdAt).format("DD-MM-YYYY HH:mm")
          : "--" || 0,
        vehicle.alerts ? vehicle.alerts.length : 0,
      ];

      // Add outer table row
      outerRowData.forEach((value, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = value;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFFF" },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Add inner table for this vehicle's alerts
      if (vehicle.alerts && vehicle.alerts.length > 0) {
        // Create inner table header
        innerHeaders.forEach((header, i) => {
          const cell = worksheet.getCell(currentRow, i + 1);
          cell.value = header;
          Object.assign(cell, {
            font: { bold: true },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "4682B4" }, // Steel Blue
            },
            alignment: {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            },
            border: borderStyle,
          });
        });
        worksheet.getRow(currentRow).height = 22;
        currentRow++;

        // Add inner table rows - Data MUST be in same order as innerHeaders
        vehicle?.alerts?.forEach((alert, alertIndex) => {
          const skipDurationFields = alert.alertType === "OVER_SPEED_ALERT";
          const innerRowData = [
            alertIndex + 1, // S. No
            alert?.alertType === "POWER_CUT"
              ? "DISCONNECTION_ALERT"
              : alert?.alertType || "N/A", // Alert Type
            getNestedValue(alert, "vehicle.site.name"), // Area
            getNestedValue(alert, "vehicle.mineName"), // Mine Name
            getNestedValue(alert, "vehicle.number"), // Vehicle Number
            alert?.sateliteCount !== undefined && alert?.sateliteCount !== null
              ? toNumberOrValue(alert.sateliteCount)
              : "---", // satelliteCounte Number
            getNestedValue(alert, "vehicle.vehicleActualTypeDTO.name"), // Vehicle Sub Type
            getNestedValue(alert, "vehicle.vehicleType.name"), // Vehicle Type
            getNestedValue(alert, "vehicle.vendor.name"), // Transporter Name

            alert.powercutRestoreTime
              ? moment(alert.powercutRestoreTime).format("DD-MM-YYYY HH:mm:ss")
              : "N/A",

            alert?.powercutRestoreLatitude && alert?.powercutRestoreLongitude
              ? `${alert.powercutRestoreLatitude} , ${alert.powercutRestoreLongitude}`
              : "N/A",

            getNestedValue(alert, "nearestLandmark") || "N/A",
            alert?.serverTime // Server Time
              ? moment(alert.serverTime).format("DD-MM-YYYY HH:mm:ss")
              : "N/A",
            alert.createdAt // Event Time
              ? moment(alert.createdAt).format("DD-MM-YYYY HH:mm:ss")
              : "N/A",
            getNestedValue(alert, "firmwareVersion"), // Firmware Version
            formatLocation(alert), // Alert Location
            skipDurationFields // GeoFence Enter Time
              ? "N/A"
              : alert?.geofenceEnterTime
                ? moment(alert.geofenceEnterTime).format("DD-MM-YYYY HH:mm:ss")
                : "N/A",
            alert?.geofenceEnterLatitude // GeoFence Enter Location
              ? `${alert?.geofenceEnterLatitude || "--"} , ${
                  alert?.geofenceEnterLongitude || "--"
                }`
              : "N/A",
            skipDurationFields // Duration
              ? "N/A"
              : alert?.geofenceEnterTime
                ? getDuration(alert?.createdAt, alert?.geofenceEnterTime)
                : "N/A",
            alert?.speed !== undefined && alert?.speed !== null
              ? toNumberOrValue(alert.speed.toFixed(2))
              : "N/A", // Speed (Km)
            alert?.reason ? alert?.reason.name : "N/A", // reason
            alert?.remarks || "N/A", // Remarks
          ];

          innerRowData.forEach((value, i) => {
            const cell = worksheet.getCell(currentRow, i + 1);
            cell.value = value;

            // 🔑 KEY FIX: Force text format for long numeric strings
            if (typeof value === "string" && /^\d{12,}$/.test(value)) {
              cell.numFmt = "@";
            }

            Object.assign(cell, {
              alignment: {
                horizontal: i === 0 ? "center" : "left", // Center S.No, left align others
                vertical: "middle",
                wrapText: true,
              },
              border: borderStyle,
            });
          });
          worksheet.getRow(currentRow).height = 18;
          currentRow++;
        });

        // Add spacing between vehicles
        currentRow++;
      } else {
        // Add spacing between vehicles even when no alerts
        currentRow++;
      }
    });

    return currentRow;
  };
  // Add this new method to handle uptime monthly export
  const generateUptimeMonthlyStructure = (worksheet, rows, startRow) => {
    let currentRow = startRow;

    // Remove header row from data and separate main data from control room data
    const headerRow = rows[0]; // First row is header
    const mainDataRows = rows.slice(1, -1); // Middle rows are main data
    const controlRoomRow = rows[rows.length - 1]; // Last row is control room

    // 🔹 FIRST TABLE: Main Data Table
    const mainHeaders = [
      "Area Name",
      "Date From",
      "Date To",
      "Total GPS Device",
      "Monthly Uptime%",
      "Monthly Downtime%",
    ];

    // Add main table header
    mainHeaders.forEach((header, i) => {
      const cell = worksheet.getCell(currentRow, i + 1);
      cell.value = header;
      Object.assign(cell, {
        font: { bold: true },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "003A6B" }, // Navy Blue
        },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: borderStyle,
      });
    });
    worksheet.getRow(currentRow).height = 22;
    currentRow++;

    // Add main table data rows
    mainDataRows.forEach((row, rowIndex) => {
      const rowData = [
        row.col1 || "",
        row.col2 || "",
        row.col3 || "",
        toNumberOrValue(row.col4),
        toNumberOrValue(row.col5),
        toNumberOrValue(row.col6),
      ];

      rowData.forEach((value, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = value;

        // 🔑 KEY FIX: Force text format for long numeric strings
        if (typeof value === "string" && /^\d{12,}$/.test(value)) {
          cell.numFmt = "@";
        }

        Object.assign(cell, {
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 20;
      currentRow++;
    });

    // Add spacing between tables
    currentRow++;

    // 🔹 SECOND TABLE: Control Room Equipment Table
    // const controlRoomHeaders = ["Date From", "Date To", "Uptime%", "Downtime%"];

    // // Add control room table header with different color
    // controlRoomHeaders?.forEach((header, i) => {
    //   const cell = worksheet.getCell(currentRow, i + 1);
    //   cell.value = header;
    //   Object.assign(cell, {
    //     font: { bold: true },
    //     fill: {
    //       type: "pattern",
    //       pattern: "solid",
    //       fgColor: { argb: "4682B4" }, // Steel Blue
    //     },
    //     alignment: { horizontal: "center", vertical: "middle", wrapText: true },
    //     border: borderStyle,
    //   });
    // });
    worksheet.getRow(currentRow).height = 22;
    currentRow++;

    // Add control room table data
    // const controlRoomData = [
    //   // controlRoomRow.col1 || "",
    //   controlRoomRow.col2 || "",
    //   controlRoomRow.col3 || "",
    //   controlRoomRow.col5 || "", // Uptime%
    //   // controlRoomRow.col4 || "", // Empty column
    //   controlRoomRow.col6 || "", // Downtime%
    // ];

    // controlRoomData.forEach((value, i) => {
    //   const cell = worksheet.getCell(currentRow, i + 1);
    //   cell.value = value;
    //   Object.assign(cell, {
    //     font: { bold: true }, // Bold for control room data
    //     alignment: {
    //       horizontal: "center",
    //       vertical: "middle",
    //       wrapText: true,
    //     },
    //     border: borderStyle,
    //   });
    // });
    // worksheet.getRow(currentRow).height = 20;
    // currentRow++;

    // Auto-fit columns for both tables
    const allHeaders = [...mainHeaders];
    allHeaders.forEach((header, colIndex) => {
      const col = worksheet.getColumn(colIndex + 1);
      let maxLen = header.toString().length;

      // Check main data
      mainDataRows.forEach((row) => {
        const rowData = [
          row.col1,
          row.col2,
          row.col3,
          row.col4,
          row.col5,
          row.col6,
        ];
        if (rowData[colIndex]) {
          maxLen = Math.max(maxLen, String(rowData[colIndex]).length);
        }
      });

      // Check control room data
      // if (controlRoomData[colIndex]) {
      //   maxLen = Math.max(maxLen, String(controlRoomData[colIndex]).length);
      // }

      col.width = Math.min(maxLen + 4, 80);
    });

    return currentRow;
  };

  // const expandTransporterHeaders = (headers) => {
  //   const expandedHeaders = [];

  //   headers.forEach((header) => {
  //     if (
  //       header.showAllDeatails &&
  //       Array.isArray(header.showAllDeatails) &&
  //       header.showAllDeatails.length > 0
  //     ) {
  //       header.showAllDeatails.forEach((detail) => {
  //         expandedHeaders.push({
  //           name: `${header.name} ${detail.name}`,
  //           keyName: detail.name,
  //           isExportShow: header.isExportShow,
  //           isShow: true,
  //         });
  //       });
  //     } else {
  //       expandedHeaders.push(header);
  //     }
  //   });

  //   return expandedHeaders;
  // };

  const handleExport = useCallback(async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName = exportHeading[0] || "Report";
      const worksheet = workbook.addWorksheet(sheetName);

      const dateData = fromDate
        ? `(From: ${moment(fromDate).format(
            "DD-MM-YYYY HH:mm:ss",
          )} To: ${moment(toDate).format("DD-MM-YYYY HH:mm:ss")})`
        : "";

      // const expandedHeaders = expandTransporterHeaders(headers);
      const visibleHeaders = headers.filter((h) => h?.isExportShow !== false);
      const colCount = Math.max(1, visibleHeaders.length);

      // Create title row (always at row 1)
      createTitleRow(
        worksheet,
        exportHeading[0] || "Report",
        colCount,
        dateData,
      );

      let currentRow = 2; // Start after title row

      // if (showQR && scannerURL) {
      //   currentRow = await addQRReferenceToWorksheet(
      //     worksheet,
      //     currentRow,
      //     colCount,
      //     workbook
      //   );
      // }

      // Add vehicle/site info row if needed
      if (vehicleNumber || siteName) {
        createVehicleSiteInfoRow(worksheet, colCount, vehicleNumber, siteName);
        currentRow++;
      }

      // Add generated rows (with Report Generated By if present)
      const rowsAdded = createGeneratedRow(
        worksheet,
        colCount,
        currentRow,
        reportGeneratedBy,
      );
      currentRow += rowsAdded;

      if (isGroupByTransporter && outerTableData.length > 0) {
        generateGroupByTransporterStructure(
          worksheet,
          outerTableData,
          outerHeaders,
          innerHeaders,
          currentRow,
        );

        // Export file and return early
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${sheetName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return; // Important: return early to prevent other code execution
      }

      // Add this new condition for NIT Report
      if (isGroupByDistanceRange && outerTableData.length > 0) {
        generateNitReportStructure(
          worksheet,
          outerTableData,
          outerHeaders,
          innerHeaders,
          currentRow,
        );

        // Export file and return early
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${sheetName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }

      // 🔹 SEPARATE CONDITION FOR UPTIME MONTHLY - Handle this first
      if (foruptimeMonthly && rows && rows.length > 0) {
        generateUptimeMonthlyStructure(worksheet, rows, currentRow);

        // Export file and return early
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${sheetName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return; // Important: return early to prevent other code execution
      }

      if (innerTableforVehicleInteractionNotesList && rows && rows.length > 0) {
        generateVehicleInteractionNotesStructure(worksheet, rows, currentRow);

        // Export file and return early
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${sheetName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }

      // In the handleExport function, add this condition after the foruptimeMonthly check
      if (multiHeader && rows && rows.length > 0) {
        generateDynamicNestedExcel(
          worksheet,
          rows,
          currentRow,
          headers,
          nestedHeadersConfig,
        );

        // Export file and return early
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${sheetName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      // 🔹 ALL EXISTING CODE BELOW REMAINS EXACTLY THE SAME
      if (
        innerTable &&
        rows &&
        rows.length > 0 &&
        rows[0].hasOwnProperty("number") &&
        rows[0].hasOwnProperty("alerts")
      ) {
        // Use inner table structure for alert data
        generateInnerTableStructure(worksheet, rows, currentRow);
      } else {
        // Use original flat table structure
        // Add Trip Summary section if enabled
        if (showTripSummary && rows.length > 0) {
          currentRow = createTripSummarySection(
            worksheet,
            rows,
            colCount,
            currentRow,
          );

          // Add spacing between summary and table
          currentRow += 2;

          // Add Tracking Points title
          worksheet.mergeCells(currentRow, 1, currentRow, colCount);
          const trackingTitleCell = worksheet.getCell(currentRow, 1);
          trackingTitleCell.value = "Tracking Points Details";
          Object.assign(trackingTitleCell, {
            font: { bold: true, size: 14, color: { argb: "0D3A6B" } },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F3FF" },
            },
            alignment: { horizontal: "center", vertical: "middle" },
            border: borderStyle,
          });
          worksheet.getRow(currentRow).height = 24;
          currentRow++;
        }

        // For trip summary, skip the first row (summary data) and use only tracking points
        const dataRows = showTripSummary ? rows.slice(1) : rows;

        createHeaderRow(worksheet, visibleHeaders, currentRow);
        currentRow++;

        createDataRows(worksheet, visibleHeaders, dataRows, currentRow);
        autoFitColumns(worksheet, visibleHeaders, dataRows);
      }

      // Export file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sheetName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      showSnackbar("Excel export failed. See console for details.", "error");
    }
  }, [
    headers,
    rows,
    exportHeading,
    fromDate,
    toDate,
    showTripSummary,
    vehicleNumber,
    siteName,
    reportGeneratedBy,
    innerTable,
    foruptimeMonthly, // Add this prop
    showSnackbar,
    innerTableforVehicleInteractionNotesList,
    isGroupByTransporter,
    outerTableData,
    outerHeaders,
    innerHeaders,
    multiHeader, // Add this
    nestedHeadersConfig, // Add this
    hierarchyList,
    // showQR,
    // scannerURL,
  ]);

  const generateVehicleInteractionNotesStructure = (
    worksheet,
    rows,
    startRow,
  ) => {
    let currentRow = startRow;

    // Outer table headers for vehicle summary
    const outerHeaders = ["S. NO", "VEHICLE NUMBER", "TOTAL REMARKS"];

    // Inner table headers for remark details
    const innerHeaders = [
      "S. No",
      "Vehicle Name",
      "Notes On Vehicle",
      "Create Time",
      "Reason",
      "Notes Created By",
    ];

    // Combine all headers to calculate consistent column widths
    const allHeaders = [...outerHeaders, ...innerHeaders];
    const columnWidths = Array(allHeaders.length).fill(0);

    // Calculate max width for each column across all data
    allHeaders.forEach((header, colIndex) => {
      let maxLen = header.toString().length;

      // Check outer table data
      rows.forEach((vehicle) => {
        const remarks = vehicle.vehicleRemarksDTOS || [];
        const outerData = [
          "", // S.No will be calculated per vehicle
          vehicle.vehicleNumber || "N/A",
          remarks.length.toString(),
        ];
        if (outerData[colIndex]) {
          maxLen = Math.max(maxLen, String(outerData[colIndex]).length);
        }

        // Check inner table data
        if (remarks.length > 0) {
          remarks.forEach((remark, remarkIndex) => {
            const innerData = [
              (remarkIndex + 1).toString(),
              remark.vehicleName || vehicle.vehicleNumber || "N/A",
              remark.remark || "N/A",
              remark.createdAt
                ? moment(remark.createdAt).format("DD-MM-YYYY HH:mm:ss")
                : "N/A",
              getNestedValue(remark, "eventId.name", "N/A"),
              remark.createdByName || "N/A",
            ];
            if (innerData[colIndex]) {
              maxLen = Math.max(maxLen, String(innerData[colIndex]).length);
            }
          });
        }
      });

      columnWidths[colIndex] = Math.min(maxLen + 4, 80);
    });

    // Apply consistent column widths
    columnWidths.forEach((width, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = width;
    });

    // Generate content for each vehicle
    rows.forEach((vehicle, vehicleIndex) => {
      const remarks = vehicle.vehicleRemarksDTOS || [];

      // Create outer table header for EACH vehicle
      outerHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "003A6B" }, // Navy Blue
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // Outer table row data
      const outerRowData = [
        vehicleIndex + 1,
        vehicle.vehicleNumber || "N/A",
        remarks.length,
      ];

      // Add outer table row
      outerRowData.forEach((value, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = value;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFFF" },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Add inner table for this vehicle's remarks
      if (remarks.length > 0) {
        // Create inner table header
        innerHeaders.forEach((header, i) => {
          const cell = worksheet.getCell(currentRow, i + 1);
          cell.value = header;
          Object.assign(cell, {
            font: { bold: true },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "4682B4" }, // Steel Blue
            },
            alignment: {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            },
            border: borderStyle,
          });
        });
        worksheet.getRow(currentRow).height = 22;
        currentRow++;

        // Add inner table rows
        remarks.forEach((remark, remarkIndex) => {
          const innerRowData = [
            remarkIndex + 1,
            remark.vehicleName || vehicle.vehicleNumber || "N/A",
            remark.remark || "N/A",
            remark.createdAt
              ? moment(remark.createdAt).format("DD-MM-YYYY HH:mm:ss")
              : "N/A",
            getNestedValue(remark, "eventId.name", "N/A"),
            remark.createdByName || "N/A",
          ];

          innerRowData.forEach((value, i) => {
            const cell = worksheet.getCell(currentRow, i + 1);
            cell.value = value;

            // 🔑 KEY FIX: Force text format for long numeric strings
            if (typeof value === "string" && /^\d{12,}$/.test(value)) {
              cell.numFmt = "@";
            }

            Object.assign(cell, {
              alignment: {
                horizontal: i === 0 ? "center" : "left", // Center S.No, left align others
                vertical: "middle",
                wrapText: true,
              },
              border: borderStyle,
            });
          });
          worksheet.getRow(currentRow).height = 18;
          currentRow++;
        });

        // Add spacing between vehicles
        currentRow++;
      } else {
        // Add spacing between vehicles even when no remarks
        currentRow++;
      }
    });

    return currentRow;
  };

  const generateDynamicNestedExcel = (
    worksheet,
    data,
    startRow,
    labelHeader,
    nestedHeadersConfig,
  ) => {
    let currentRow = startRow;

    const hierarchy =
      hierarchyList?.length > 0
        ? hierarchyList
        : [
            { key: "mines", label: "Mines", color: [15, 93, 63] },
            {
              key: "siteCategory",
              label: "Site Categories",
              color: [139, 46, 15],
            },
            { key: "siteInfoDTOs", label: "Sites", color: [90, 46, 166] },
          ];

    // ✅ HEADER COLORS (DARK)
    const levelColors = {
      0: "1F2A44", // Region
      1: "0F5D3F", // Mine
      2: "8B2E0F", // Category
      3: "4682B4", // Site
    };

    // ✅ ROW COLORS (LIGHT like UI)
    const levelRowColors = {
      0: "D9E1F2",
      1: "E2EFDA",
      2: "FCE4D6",
      3: "D6EAF8",
    };

    const getHeaders = (level) => {
      if (level === 0) {
        return labelHeader.filter((h) => h?.isShow !== false);
      }
      const hierarchyKey = hierarchy[level - 1]?.key;
      return (nestedHeadersConfig[hierarchyKey] || []).filter(
        (h) => h?.isShow !== false,
      );
    };

    // Helper to format cell value
    const formatCellValueForExcel = (row, header, rowIndex) => {
      if (header.sNo || header.keyName === "sNo") {
        return rowIndex + 1;
      }
      if (header.isActive && header.keyName === "isActive") {
        return row[header.keyName] ? "Active" : "InActive";
      }
      const value = row[header.keyName];
      if (value === undefined || value === null) return "";

      if (header?.keyName === "tripStatus" || header?.isBox) {
        return mapStatusValue(value);
      }

      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (header.isDate && value) {
        return moment(value).format("DD-MM-YYYY HH:mm:ss");
      }
      return toNumberOrValue(value);
    };

    // ✅ HEADER STYLE
    const applyHeaderStyle = (cell, level) => {
      cell.font = { bold: true, color: { argb: "FFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: levelColors[level] || "4682B4" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = borderStyle;
    };

    // ✅ CELL STYLE (WITH ROW COLOR)
    const applyCellStyle = (cell, level, rowIndex) => {
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };

      cell.border = borderStyle;

      // ✅ Row color (alternate optional)
      const baseColor = levelRowColors[level] || "FFFFFF";
      const finalColor = rowIndex % 2 === 0 ? baseColor : "FFFFFF";

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: finalColor },
      };
    };

    // ✅ AUTO WIDTH
    const setColumnWidth = (headersList, startCol = 0) => {
      headersList.forEach((h, i) => {
        const col = worksheet.getColumn(startCol + i + 1);
        const currentWidth = col.width || 10;
        const newWidth = Math.max(currentWidth, (h.name?.length || 10) + 10);
        col.width = Math.min(newWidth, 80);
      });
    };

    // ✅ HEADER RENDER
    const renderHeader = (headersList, level) => {
      setColumnWidth(headersList);

      headersList.forEach((h, colIndex) => {
        const cell = worksheet.getCell(currentRow, colIndex + 1);
        cell.value = h.name || h.keyName || "";
        applyHeaderStyle(cell, level);
      });

      currentRow++;
    };

    // ✅ MAIN RECURSION
    const renderRows = (rowsData, level = 0, parent = {}) => {
      if (!Array.isArray(rowsData) || rowsData.length === 0) return;

      let headersList = getHeaders(level);
      if (!headersList.length) return;

      // 🔥 HEADER
      renderHeader(headersList, level);

      rowsData.forEach((row, rowIndex) => {
        headersList.forEach((header, colIndex) => {
          const cell = worksheet.getCell(currentRow, colIndex + 1);

          let value = formatCellValueForExcel(row, header, rowIndex);

          // Add indentation for nested levels (first data column)
          if (colIndex === 0 && level > 0) {
            value = "  ".repeat(level) + value;
          }

          cell.value = value ?? "";

          // 🔑 KEY FIX: Force text format for long numeric strings
          if (typeof value === "string" && /^\d{12,}$/.test(value)) {
            cell.numFmt = "@";
          }

          // ✅ APPLY STYLE
          applyCellStyle(cell, level, rowIndex);

          // ✅ NEGATIVE VALUE → RED
          if (typeof value === "number" && value < 0) {
            cell.font = { color: { argb: "FF0000" }, bold: true };
          }

          // ✅ CENTER NUMBERS
          if (typeof value === "number") {
            cell.alignment.horizontal = "center";
          }

          // ✅ BOLD S.NO
          if (colIndex === 0) {
            cell.font = { ...(cell.font || {}), bold: true };
          }
        });

        currentRow++;

        // 🔁 CHILD LEVEL
        const nextLevel = hierarchy[level];
        if (nextLevel && Array.isArray(row[nextLevel.key])) {
          renderRows(row[nextLevel.key], level + 1, {
            ...parent,
            ...row,
          });
        }
      });

      currentRow++; // spacing between groups
    };

    // ✅ START
    renderRows(data, 0);

    // ✅ FREEZE HEADER (optional)
    worksheet.views = [{ state: "frozen", ySplit: startRow }];

    return currentRow;
  };

  const generateGroupByTransporterStructure = (
    worksheet,
    outerTableData,
    outerHeaders,
    innerHeaders,
    startRow,
  ) => {
    let currentRow = startRow;

    // Prepare outer headers (for transporter summary)
    const formattedOuterHeaders = outerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Prepare inner headers (for trip details)
    const formattedInnerHeaders = innerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Combine all headers to calculate consistent column widths
    const allHeaders = [...formattedOuterHeaders, ...formattedInnerHeaders];
    const columnWidths = Array(allHeaders.length).fill(0);

    // Calculate max width for each column across all data
    allHeaders.forEach((header, colIndex) => {
      let maxLen = header.toString().length;

      // Check outer table data
      outerTableData.forEach((transporter, transporterIndex) => {
        const trips = transporter.alerts || [];
        const outerData = [
          (transporterIndex + 1).toString(),
          transporter.vendorName || "N/A",
          transporter.totalTrips?.toString() || "0",
        ];

        if (outerData[colIndex]) {
          maxLen = Math.max(maxLen, String(outerData[colIndex]).length);
        }

        // Check inner table data
        trips.forEach((trip, tripIndex) => {
          const innerData = formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";
            return formatCellValue(trip, headerConfig, tripIndex);
          });

          if (innerData[colIndex - formattedOuterHeaders.length]) {
            maxLen = Math.max(
              maxLen,
              String(innerData[colIndex - formattedOuterHeaders.length]).length,
            );
          }
        });
      });

      columnWidths[colIndex] = Math.min(maxLen + 4, 80);
    });

    // Apply consistent column widths
    columnWidths.forEach((width, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = width;
    });

    // Generate content for each transporter
    outerTableData.forEach((transporter, transporterIndex) => {
      const trips = transporter.alerts || [];

      // Create outer table header for EACH transporter
      formattedOuterHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "003A6B" }, // Navy Blue
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // Outer table row data
      const outerRowData = [
        transporterIndex + 1,
        transporter.vendorName || "N/A",
        toNumberOrValue(transporter.totalTrips),
      ];

      // Add outer table row with light blue background
      outerRowData.forEach((value, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = value;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "F0F8FF" }, // Light blue background for transporter row
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Add inner table for this transporter's trips
      if (trips.length > 0) {
        // Add a subtitle for the inner table

        // Create inner table header
        formattedInnerHeaders.forEach((header, i) => {
          const cell = worksheet.getCell(currentRow, i + 1);
          cell.value = header;
          Object.assign(cell, {
            font: { bold: true },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "4682B4" }, // Steel Blue
            },
            alignment: {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            },
            border: borderStyle,
          });
        });
        worksheet.getRow(currentRow).height = 22;
        currentRow++;

        // Add inner table rows
        trips.forEach((trip, tripIndex) => {
          const innerRowData = formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";

            let cellValue = formatCellValue(trip, headerConfig, tripIndex);
            if (headerConfig?.keyName === "tripStatus" || headerConfig?.isBox) {
              cellValue = mapStatusValue(cellValue);
            }

            return toNumberOrValue(cellValue);
          });

          innerRowData.forEach((value, i) => {
            const cell = worksheet.getCell(currentRow, i + 1);
            cell.value = value;

            // 🔑 KEY FIX: Force text format for long numeric strings
            if (typeof value === "string" && /^\d{12,}$/.test(value)) {
              cell.numFmt = "@";
            }

            Object.assign(cell, {
              alignment: {
                horizontal: "center",
                vertical: "middle",
                wrapText: true,
              },
              border: borderStyle,
            });
          });
          worksheet.getRow(currentRow).height = 18;
          currentRow++;
        });

        // Add spacing between transporters
        currentRow += 2;
      } else {
        // No trips for this transporter
        const noTripsCell = worksheet.getCell(currentRow, 1);
        noTripsCell.value = "No trips found for this transporter";
        Object.assign(noTripsCell, {
          font: { italic: true, size: 10, color: { argb: "808080" } },
        });
        currentRow += 3;
      }
    });

    return currentRow;
  };
  const generateNitReportStructure = (
    worksheet,
    outerTableData,
    outerHeaders,
    innerHeaders,
    startRow,
  ) => {
    let currentRow = startRow;

    // Prepare outer headers (for NIT summary)
    const formattedOuterHeaders = outerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Prepare inner headers (for distance range details)
    const formattedInnerHeaders = innerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Combine all headers to calculate consistent column widths
    const allHeaders = [...formattedOuterHeaders, ...formattedInnerHeaders];
    const columnWidths = Array(allHeaders.length).fill(0);

    // Calculate max width for each column across all data
    allHeaders.forEach((header, colIndex) => {
      let maxLen = header.toString().length;

      // Check outer table data
      outerTableData.forEach((nitRecord, nitIndex) => {
        const distanceRanges = nitRecord.alerts || [];
        const outerData = [
          nitIndex + 1,
          nitRecord.mineName || "N/A",
          nitRecord.nitNo || "N/A",
          nitRecord.contractorName || "N/A",
          toNumberOrValue(nitRecord.totalAllowedTippers),
          toNumberOrValue(nitRecord.totalTippersWithGps),
          toNumberOrValue(nitRecord.totalVehiclesWithoutGps),
          toNumberOrValue(nitRecord.noOfValidTripsDiesel),
          toNumberOrValue(nitRecord.totalDistanceOfValidTrips),
          toNumberOrValue(nitRecord.noOfInvalidTrips),
          toNumberOrValue(nitRecord.totalDistanceOfInvalidTrips),
        ];

        if (outerData[colIndex]) {
          maxLen = Math.max(maxLen, String(outerData[colIndex]).length);
        }

        // Check inner table data
        distanceRanges.forEach((range, rangeIndex) => {
          const innerData = formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";

            // Handle S.No column
            if (headerConfig.sNo) {
              return rangeIndex + 1;
            }

            // Handle the range column
            if (headerConfig.keyName === "range") {
              return range.range || "N/A";
            }

            // Handle other columns based on keyName
            switch (headerConfig.keyName) {
              case "validTripCount":
                return toNumberOrValue(range.validTripCount);
              case "validTotalDistance":
                return toNumberOrValue(range.validTotalDistance);
              case "noOfInvalidTrips":
                return toNumberOrValue(range.noOfInvalidTrips);
              case "totalDistanceOfInvalidTrips":
                return toNumberOrValue(range.totalDistanceOfInvalidTrips);
              case "inValidtripCount":
                return toNumberOrValue(range.inValidtripCount);
              case "inValidtotalDistance":
                return toNumberOrValue(range.inValidtotalDistance);
              default:
                return getNestedValue(range, headerConfig.keyName) || "N/A";
            }
          });

          if (innerData[colIndex - formattedOuterHeaders.length]) {
            maxLen = Math.max(
              maxLen,
              String(innerData[colIndex - formattedOuterHeaders.length]).length,
            );
          }
        });
      });

      columnWidths[colIndex] = Math.min(maxLen + 4, 80);
    });

    // Apply consistent column widths
    columnWidths.forEach((width, colIndex) => {
      worksheet.getColumn(colIndex + 1).width = width;
    });

    // Generate content for each NIT record
    outerTableData.forEach((nitRecord, nitIndex) => {
      const distanceRanges = nitRecord.alerts || [];

      // Check if we need to add a new page (for large datasets)
      if (currentRow > 1048575) {
        // Excel row limit
        return;
      }

      // Create outer table header for EACH NIT record
      formattedOuterHeaders.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        Object.assign(cell, {
          font: { bold: true, color: { argb: "FFFFFF" } },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "003A6B" }, // Navy Blue
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 22;
      currentRow++;

      // Outer table row data
      // Outer table row data
      const outerRowData = [
        nitIndex + 1,
        nitRecord.mineName || "N/A",
        nitRecord.nitNo || "N/A",
        nitRecord.contractorName || "N/A",
        toNumberOrValue(nitRecord.totalAllowedTippers),
        toNumberOrValue(nitRecord.totalTippersWithGps),
        toNumberOrValue(nitRecord.totalVehiclesWithoutGps),
        toNumberOrValue(nitRecord.noOfValidTripsDiesel),
        toNumberOrValue(nitRecord.totalDistanceOfValidTrips),
        toNumberOrValue(nitRecord.noOfInvalidTrips),
        toNumberOrValue(nitRecord.totalDistanceOfInvalidTrips),
      ];

      // Add outer table row with light blue background
      outerRowData.forEach((value, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = value;
        Object.assign(cell, {
          font: { bold: true },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "F0F8FF" }, // Light blue background for NIT row
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: borderStyle,
        });
      });
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Add inner table for this NIT's distance ranges
      if (distanceRanges.length > 0) {
        // Create inner table header
        formattedInnerHeaders.forEach((header, i) => {
          const cell = worksheet.getCell(currentRow, i + 1);
          cell.value = header;
          Object.assign(cell, {
            font: { bold: true, color: { argb: "FFFFFF" } },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "4682B4" }, // Steel Blue
            },
            alignment: {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            },
            border: borderStyle,
          });
        });
        worksheet.getRow(currentRow).height = 22;
        currentRow++;

        // Add inner table rows
        distanceRanges.forEach((range, rangeIndex) => {
          const innerRowData = formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";

            // Handle S.No column
            if (headerConfig.sNo) {
              return rangeIndex + 1;
            }

            // Handle the range column
            if (headerConfig.keyName === "range") {
              return range.range || "N/A";
            }

            // Handle other columns based on keyName
            switch (headerConfig.keyName) {
              case "validTripCount":
                return toNumberOrValue(range.validTripCount);
              case "validTotalDistance":
                return toNumberOrValue(range.validTotalDistance);
              case "noOfInvalidTrips":
                return toNumberOrValue(range.noOfInvalidTrips);
              case "totalDistanceOfInvalidTrips":
                return toNumberOrValue(range.totalDistanceOfInvalidTrips);
              case "inValidtripCount":
                return toNumberOrValue(range.inValidtripCount);
              case "inValidtotalDistance":
                return toNumberOrValue(range.inValidtotalDistance);
              default:
                return getNestedValue(range, headerConfig.keyName) || "N/A";
            }
          });

          innerRowData.forEach((value, i) => {
            const cell = worksheet.getCell(currentRow, i + 1);
            cell.value = value;

            // 🔑 KEY FIX: Force text format for long numeric strings
            if (typeof value === "string" && /^\d{12,}$/.test(value)) {
              cell.numFmt = "@";
            }

            Object.assign(cell, {
              alignment: {
                horizontal: "center",
                vertical: "middle",
                wrapText: true,
              },
              border: borderStyle,
            });
          });
          worksheet.getRow(currentRow).height = 18;
          currentRow++;
        });

        // Add spacing between NIT records
        currentRow += 2;
      } else {
        // No distance ranges for this NIT
        const noDataCell = worksheet.getCell(currentRow, 1);
        noDataCell.value = "No distance ranges found for this NIT";
        Object.assign(noDataCell, {
          font: { italic: true, size: 10, color: { argb: "808080" } },
          alignment: { horizontal: "left", vertical: "middle" },
          border: borderStyle,
        });
        worksheet.mergeCells(
          currentRow,
          1,
          currentRow,
          formattedOuterHeaders.length,
        );
        currentRow += 3;
      }
    });

    return currentRow;
  };

  // return (
  //   <div
  //     id="styleExportExcel"
  //     onClick={handleExport}
  //     style={{ cursor: "pointer" }}
  //   >
  //     <img src={excelIcon} alt="Export to Excel" />
  //   </div>
  // );

  return (
    <div
      id="styleExportExcel"
      onClick={handleExport}
      title="Export to Excel"
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleExport();
        }
      }}
      style={{
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        minWidth: 40,
        minHeight: 36,
        padding: "6px 10px",
        borderRadius: 5,
        backgroundColor: "#009d08",
      }}
    >
      <img
        src={excelIcon}
        alt="Export to Excel"
        style={{
          display: "block",
          width: 24,
          height: 24,
          objectFit: "contain",
        }}
      />
    </div>
  );
};

export default CommanExcelExport;
